// The one grid every screen is built from.
//
// WHY IT IS HAND-WRITTEN OVER TANSTACK RATHER THAN BOUGHT. Two-level row
// grouping with aggregates and inline editing is the requirement that decides
// the library, and AG Grid puts row grouping behind its Enterprise licence.
// TanStack Table does grouping and aggregation in its MIT core but renders
// nothing, so the group rows, the editing, the virtual scrolling and the
// selection below are this file's to write. That is the trade that was taken:
// a few hundred lines here against a purchased licence and a licence key in the
// bundle.
//
// EVERY SCREEN IS THIS COMPONENT. Fifteen authored views, and the differences
// between them are which rows, which columns, what to group by and what happens
// when a row is tapped -- all four of which are arguments. A second grid
// implementation is how two screens start disagreeing about what a group header
// counts.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type Row as TableRow,
  type SortingState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { FieldType } from '../schema';
import type { Row } from '../types';
import { dateInputValue, formatBool, formatDate, formatNumber, groupLabel } from '../format';
import { EditableCell } from './EditableCell';

export interface GridColumn<T> {
  /** The column's key, which is a column name where the value is one and a
   *  made-up id where the value is derived -- `action_category`, `owners`. */
  key: string;
  label: string;
  type?: FieldType;
  width?: number;
  /** Static column styling. Six of the source's fourteen conditional-formatting
   *  rules have no condition in them at all -- Action Category bold, Products
   *  red, Plant names blue, Commodities green, Departments teal -- so they are
   *  a class on the column rather than a rule evaluated per row. */
  className?: string;
  /** Derived value. Where absent the value is `row[key]`. */
  value?: (row: T) => unknown;
  /** Custom rendering, after `value`. */
  render?: (row: T) => ReactNode;
  /** Conditional formatting that does depend on the row -- the only one in the
   *  application is Daily Production's four columns against `Daily Target`. */
  cellClass?: (row: T) => string | undefined;
  editable?: boolean;
  /** A foreign key's choices, which turn its editor into a select. */
  options?: { value: string | number; label: string }[];
  /** The column a PATCH names, where it is not the column being shown. A
   *  barrier view resolves `commodity_id` into `commodity_name` for reading,
   *  and the write still has to name the key -- so `Commodity` shows the name
   *  and edits the id. */
  editKey?: string;
  editFrom?: (row: T) => unknown;
  /** Shown on the group header. `sum` is the only one the application has:
   *  `SUM(Outlook Build)` on the Two Week Production View. */
  aggregate?: 'sum';
  align?: 'right';
}

export interface DataGridProps<T extends Row> {
  rows: T[];
  columns: GridColumn<T>[];
  getRowId: (row: T) => string;
  /** One or two column keys. Two is not a theoretical maximum -- `Department`
   *  groups by Owner Department then Commodity and `Interplant Transfers` by
   *  Product then Job Number -- but nothing in the application needs three. */
  groupBy?: string[];
  groupDesc?: boolean[];
  sortBy?: { key: string; desc?: boolean }[];
  /** The hot-row highlight. The source spans that over 30 individual column
   *  rules; here it is one class on the row. */
  rowClass?: (row: T) => string | undefined;
  onRowClick?: (row: T) => void;
  /** Inline (quick) edit, enabled on every barrier grid except Plants. Returns
   *  when the write has been accepted, so the cell can show a failure. */
  onEdit?: (row: T, key: string, value: unknown) => Promise<void>;
  selectedId?: string | null;
  onSelect?: (row: T | null) => void;
  actions?: (row: T) => ReactNode;
  emptyMessage?: string;
  height?: number;
}

const ROW_HEIGHT = 34;
/** The narrowest a column with no `width` of its own may be. */
const FLEX_MIN_WIDTH = 120;
const ACTIONS_WIDTH = 96;
/** How far each level of grouping indents the rows beneath it. */
const GROUP_INDENT = 16;

export function DataGrid<T extends Row>({
  rows,
  columns,
  getRowId,
  groupBy = [],
  groupDesc = [],
  sortBy = [],
  rowClass,
  onRowClick,
  onEdit,
  selectedId,
  onSelect,
  actions,
  emptyMessage = 'Nothing to show.',
  height,
}: DataGridProps<T>) {
  const [error, setError] = useState<string | null>(null);

  const columnDefs = useMemo<ColumnDef<T>[]>(
    () =>
      columns.map((column) => ({
        id: column.key,
        header: column.label,
        accessorFn: (row: T) => (column.value ? column.value(row) : row[column.key]),
        // `sum` over a column that is null on some rows sums the rest, which is
        // what the two-week view wants.
        aggregationFn: column.aggregate === 'sum' ? 'sum' : undefined,
        sortingFn: column.type === 'int' ? 'basic' : 'alphanumeric',
        enableGrouping: true,
        meta: column,
      })),
    [columns]
  );

  // Grouping and sorting are one thing to TanStack: a grouped column's sort
  // direction is the order its groups appear in, which is what lets
  // `Barriers Dashboard` say "group by Action Category descending, sort by
  // Commodity ascending" as one sorting state.
  const sorting = useMemo<SortingState>(
    () => [
      ...groupBy.map((key, index) => ({ id: key, desc: groupDesc[index] ?? false })),
      ...sortBy.map((s) => ({ id: s.key, desc: s.desc ?? false })),
    ],
    [groupBy, groupDesc, sortBy]
  );

  const table = useReactTable({
    data: rows,
    columns: columnDefs,
    state: { grouping: groupBy, sorting, expanded: true },
    getRowId: (row) => getRowId(row),
    getCoreRowModel: getCoreRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Groups open. The source's grouped views open expanded and a screen that
    // opened collapsed would hide every row behind a click.
    autoResetExpanded: false,
    manualPagination: true,
  });

  const modelRows = table.getRowModel().rows;

  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: modelRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  useEffect(() => {
    virtualizer.measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelRows.length]);

  async function commit(row: T, key: string, value: unknown): Promise<void> {
    if (!onEdit) return;
    try {
      setError(null);
      await onEdit(row, key, value);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
      throw caught;
    }
  }

  const template = columns
    .map((c) => (c.width ? `${c.width}px` : `minmax(${FLEX_MIN_WIDTH}px, 1fr)`))
    .concat(actions ? [`${ACTIONS_WIDTH}px`] : [])
    .join(' ');

  // The narrowest the columns can be, which the header and every row are held
  // to together. Without it each is as wide as the screen and its cells spill
  // past it, so a scrolled grid shows cells with no row behind them. A leaf
  // row's indent is inside its width, so it is counted too.
  const minWidth =
    columns.reduce((sum, c) => sum + (c.width ?? FLEX_MIN_WIDTH), 0) +
    (actions ? ACTIONS_WIDTH : 0) +
    groupBy.length * GROUP_INDENT;

  return (
    <div className="grid" style={height ? { height } : undefined}>
      {error && <div className="grid-error">{error}</div>}
      {/* The header is inside the scrolling body, so a grid wider than the
          screen scrolls its header sideways with its rows; `position: sticky`
          keeps it in place as the rows scroll beneath. The virtualiser is not
          told it is there, and need not be: the rows that leaves unrendered at
          the top are the ones the header covers. */}
      <div className="grid-body" ref={scrollRef}>
        <div style={{ minWidth }}>
          <div className="grid-head" style={{ gridTemplateColumns: template }}>
            {columns.map((column) => (
              <div key={column.key} className={cx('grid-cell', column.align === 'right' && 'right')}>
                {column.label}
              </div>
            ))}
            {actions && <div className="grid-cell" />}
          </div>

          {modelRows.length === 0 ? (
            <div className="grid-empty">{emptyMessage}</div>
          ) : (
            <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
              {virtualizer.getVirtualItems().map((virtualRow) => {
                const modelRow = modelRows[virtualRow.index];
                return (
                  <div
                    key={modelRow.id}
                    ref={virtualizer.measureElement}
                    data-index={virtualRow.index}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    {modelRow.getIsGrouped() ? (
                      <GroupRow row={modelRow} columns={columns} template={template} hasActions={Boolean(actions)} />
                    ) : (
                      <LeafRow
                        row={modelRow}
                        columns={columns}
                        template={template}
                        depth={groupBy.length}
                        rowClass={rowClass}
                        onRowClick={onRowClick}
                        onEdit={onEdit ? commit : undefined}
                        selected={selectedId != null && selectedId === getRowId(modelRow.original)}
                        onSelect={onSelect}
                        actions={actions}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GroupRow<T extends Row>({
  row,
  columns,
  template,
  hasActions,
}: {
  row: TableRow<T>;
  columns: GridColumn<T>[];
  template: string;
  hasActions: boolean;
}) {
  const column = columns.find((c) => c.key === row.groupingColumnId);
  const noun = column?.label ?? 'value';
  // `getLeafRows` flattens the subtree and keeps the intermediate group rows in
  // it, so on a two-level view an outer header counts its inner headers as
  // well -- three barriers under two job numbers came back as five. The count a
  // person reads is the number of records.
  const records = row.getLeafRows().filter((leaf) => !leaf.getIsGrouped()).length;
  return (
    <div
      className={cx('grid-row', 'group', `depth-${row.depth}`)}
      style={{ gridTemplateColumns: template }}
      role="row"
    >
      {columns.map((c, index) =>
        index === 0 ? (
          <div key={c.key} className="grid-cell group-label" style={{ paddingLeft: 8 + row.depth * GROUP_INDENT }}>
            <span className={cx('group-name', column?.className)}>
              {/* Formatted by the grouping column's own type, exactly as the
                  cells beneath are. Without this a week grouped as a date read
                  `2026-06-29` in the header and `6/29/2026` in every row. */}
              {groupLabel(row.getGroupingValue(row.groupingColumnId!), noun, column?.type)}
            </span>
            {/* The count is on every group header without being asked for. It is
                what `Barriers Dashboard` and `HOT` name as their aggregate, and
                on a view that names none it is still the number a person reads
                first. */}
            <span className="group-count">{records}</span>
          </div>
        ) : (
          <div key={c.key} className={cx('grid-cell', 'group-aggregate', c.align === 'right' && 'right')}>
            {c.aggregate === 'sum' ? formatNumber(row.getValue(c.key)) : ''}
          </div>
        )
      )}
      {hasActions && <div className="grid-cell" />}
    </div>
  );
}

function LeafRow<T extends Row>({
  row,
  columns,
  template,
  depth,
  rowClass,
  onRowClick,
  onEdit,
  selected,
  onSelect,
  actions,
}: {
  row: TableRow<T>;
  columns: GridColumn<T>[];
  template: string;
  depth: number;
  rowClass?: (row: T) => string | undefined;
  onRowClick?: (row: T) => void;
  onEdit?: (row: T, key: string, value: unknown) => Promise<void>;
  selected: boolean;
  onSelect?: (row: T | null) => void;
  actions?: (row: T) => ReactNode;
}) {
  const original = row.original;
  return (
    <div
      className={cx('grid-row', selected && 'selected', rowClass?.(original))}
      style={{ gridTemplateColumns: template, paddingLeft: depth * GROUP_INDENT }}
      role="row"
      onClick={() => {
        // Selecting is what cross-filters a dashboard; opening is what a
        // barrier grid does. A screen passes one or the other, never both, so
        // a tap is never ambiguous.
        if (onSelect) onSelect(selected ? null : original);
        else onRowClick?.(original);
      }}
    >
      {columns.map((column) => {
        const value = column.value ? column.value(original) : original[column.key];
        return (
          <div
            key={column.key}
            className={cx('grid-cell', column.className, column.cellClass?.(original), column.align === 'right' && 'right')}
          >
            {column.editable && onEdit ? (
              <EditableCell
                value={column.editFrom ? column.editFrom(original) : value}
                display={column.editFrom ? renderValue(value, 'str') : undefined}
                type={column.type ?? 'str'}
                options={column.options}
                onCommit={(next) => onEdit(original, column.editKey ?? column.key, next)}
              />
            ) : (
              column.render?.(original) ?? renderValue(value, column.type)
            )}
          </div>
        );
      })}
      {actions && (
        <div className="grid-cell actions" onClick={(event) => event.stopPropagation()}>
          {actions(original)}
        </div>
      )}
    </div>
  );
}

export function renderValue(value: unknown, type?: FieldType): string {
  switch (type) {
    case 'date':
      return formatDate(value);
    case 'int':
      return formatNumber(value);
    case 'bool':
      return formatBool(value);
    default:
      return value === null || value === undefined ? '' : String(value);
  }
}

export function editValue(value: unknown, type: FieldType): string {
  return type === 'date' ? dateInputValue(value) : value === null || value === undefined ? '' : String(value);
}

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}
